<script>
import debounce from "debounce";
import { mapGetters } from "vuex";
import Spinner from "@/components/ui/Spinner";
import pagination from "@/mixins/pagination";
import imageService from "@/services/image";
import ModalMedicalPractice from "./ModalMedicalPractice.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "MedicalPracticeList",

  mixins: [pagination],

  components: {
    Spinner,
    ModalMedicalPractice,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      categories: [],
      selectedCategory: "",
      statusFilter: "active",
      practices: [],
      isModalVisible: false,
      selectedPractice: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    canManagePractices() {
      return this.isAdmin;
    },
  },

  created() {
    this.loadCategories();
    this.getPractices();

    this.$bus.$on("medical-practice-created", this.onPracticeCreated);
    this.$bus.$on("medical-practice-updated", this.onPracticeUpdated);
  },

  beforeDestroy() {
    this.$bus.$off("medical-practice-created", this.onPracticeCreated);
    this.$bus.$off("medical-practice-updated", this.onPracticeUpdated);
  },

  methods: {
    async loadCategories() {
      try {
        const categories = await imageService.getCategories();
        this.categories = [
          ...new Set(categories.map((c) => String(c).trim())),
        ].sort((a, b) => a.localeCompare(b));
      } catch (error) {
        this.categories = [];
      }
    },

    isActiveFilterValue() {
      if (this.statusFilter === "active") return true;
      if (this.statusFilter === "inactive") return false;
      return undefined;
    },

    async getPractices(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        search: this.searchInput,
        category: this.selectedCategory,
        isActive: this.isActiveFilterValue(),
        ...query,
      };

      try {
        const res = await imageService.getImagesPaginated(defaultQuery);
        this.practices = res.docs;
        this.pagination.total = res.total;
      } finally {
        this.isLoading = false;
      }
    },

    resetToFirstPage(extra = {}) {
      this.pagination.currentPage = 1;
      this.pagination.offset = 0;
      this.getPractices({ offset: 0, ...extra });
    },

    debounceInput: debounce(function debounceInput() {
      this.resetToFirstPage();
    }, DEBOUNCE_MS_TIMEOUT),

    onFilterChange() {
      this.resetToFirstPage();
    },

    onPageChange() {
      this.getPractices();
    },

    onPracticeCreated(practice) {
      this.practices.unshift(practice);
      this.pagination.total += 1;
    },

    onPracticeUpdated(updatedPractice) {
      const index = this.practices.findIndex(
        (p) => p._id === updatedPractice._id
      );
      if (index !== -1) {
        this.$set(this.practices, index, updatedPractice);
      }
    },

    showPracticeModal(practice) {
      this.selectedPractice = practice || {};
      this.isModalVisible = true;
    },

    closePracticeModal() {
      this.isModalVisible = false;
      this.selectedPractice = {};
    },

    toggleActive(practice) {
      const nextIsActive = !practice.isActive;
      const actionLabel = nextIsActive ? "reactivar" : "dar de baja a";

      this.$confirm(
        `¿Desea ${actionLabel} la práctica "${practice.description}"?`,
        "Confirmar",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() =>
          imageService.updateImage(practice._id, { isActive: nextIsActive })
        )
        .then((updated) => this.onPracticeUpdated(updated))
        .catch(() => {});
    },
  },
};
</script>

<template lang="pug">
  section.medical-practice-list
    header.headline
      .headline__actions
        el-input.search__input(
          prefix-icon="el-icon-search"
          @input="debounceInput"
          v-model="searchInput"
          placeholder="Buscar por código o descripción"
          :clearable="true"
        )
        el-select(
          v-model="selectedCategory"
          placeholder="Categoría"
          filterable
          clearable
          @change="onFilterChange"
        )
          el-option(v-for="category in categories" :key="category" :label="category" :value="category")
        el-select(v-model="statusFilter" @change="onFilterChange")
          el-option(label="Activas" value="active")
          el-option(label="Inactivas" value="inactive")
          el-option(label="Todas" value="all")
        .new_medical-practice.button.button--blue.box__icon(
          v-if="canManagePractices"
          @click='showPracticeModal()'
        )
          micon(name="add_circle_outline").button__icon
          span.button__text Nueva Práctica
    .box
      .box__content--stretch
        table
          thead
            tr
              th Código
              th Descripción
              th Categoría
              th Estado
              th Acciones
          tbody
            spinner(v-if="isLoading")
            tr(v-else v-for="practice in practices" :key="practice._id" @click='showPracticeModal(practice)')
              td {{ practice.code }}
              td {{ practice.description }}
              td {{ practice.category }}
              td
                el-tag(:type="practice.isActive ? 'success' : 'danger'" size="small")
                  | {{ practice.isActive ? 'Activa' : 'Inactiva' }}
              td.actions
                .actions-container(v-if="canManagePractices")
                  el-dropdown(trigger="click")
                    .button.button--action.el-dropdown-link(@click.stop)
                      micon(name="more_horiz")
                    el-dropdown-menu(slot="dropdown")
                      el-dropdown-item
                        div(@click="showPracticeModal(practice)")
                          micon(name="edit")
                          | Editar práctica
                      el-dropdown-item
                        div(@click.stop="toggleActive(practice)")
                          micon(:name="practice.isActive ? 'block' : 'check_circle'")
                          | {{ practice.isActive ? 'Dar de baja' : 'Reactivar' }}

      modal-medical-practice(
        v-if='isModalVisible'
        :current-practice='selectedPractice'
        :show-dialog-modal='isModalVisible'
        @close='closePracticeModal'
      )

      pagination(
        :isLoading="isLoading"
        :limit="pagination.limit"
        :total="pagination.total"
        @pagination="setPagination"
      )

</template>

<style lang="scss" scoped>
@import "@/styles/settings/index.scss";
@import "@/styles/tools/index.scss";

tbody td:hover {
  cursor: pointer;
}

.box {
  height: 100%;
}

.medical-practice-list {
  height: 65vh;
  .headline {
    .headline__actions {
      @include media(mobile-up) {
        width: 100%;
      }
      @include media(web-up) {
        width: auto;
      }
    }
  }
}
</style>
